import type React from 'react';
import { Suspense, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from '@/components/sidebar';
import { Header } from '@/components/header';
import { ViewProvider } from '@/contexts/view-context';
import { TrainerProvider } from '@/contexts/trainer-context';
import { MobileProvider } from '@/contexts/mobile-context';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { ConfirmationProvider } from '@/components/providers/confirmation-provider';
import { FixPointerEventsButton } from '@/components/debug/fix-pointer-events-button';
import { useServerEvents } from '@/hooks/useServerEvents';

function DashboardContent({ children }: { children: React.ReactNode }) {
  useServerEvents();
  const [showDebugButton, setShowDebugButton] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const timer = setTimeout(() => setShowDebugButton(true), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Full-height fixed rail — owns the brand logo and navigation */}
      <div className="fixed inset-y-0 left-0 z-30 hidden w-[240px] md:block">
        <Sidebar className="h-full" />
      </div>
      {/* Content column, offset by the rail on desktop */}
      <div className="flex min-h-screen flex-col md:ml-[240px]">
        <Header />
        <main className="flex-1 overflow-auto p-4 sm:p-6 md:p-8">
          <div className="mx-auto w-full max-w-[1400px]">
            <Suspense
              fallback={
                <div className="flex h-[60vh] items-center justify-center">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-primary" />
                </div>
              }
            >
              {children}
            </Suspense>
          </div>
        </main>
      </div>
      <ConfirmationProvider />
      {showDebugButton && <FixPointerEventsButton />}
    </div>
  );
}

export default function DashboardLayout() {
  return (
    <ProtectedRoute>
      <ViewProvider>
        <TrainerProvider>
          <MobileProvider>
            <DashboardContent>
              <Outlet />
            </DashboardContent>
          </MobileProvider>
        </TrainerProvider>
      </ViewProvider>
    </ProtectedRoute>
  );
}
